import { getMarkerBackgroundColor, getMarkerColorScale } from '@weenat/client/dist/core/map'
import {
  ClusterAggregateType,
  ClusterDisplayValue,
  ClusterProperties,
  getZIndex
} from '@weenat/client/dist/core/measurements'
import { isFreemiumAccessibleAggregate } from '@weenat/client/dist/core/universes'
import { useConvertedValue } from '@weenat/client/dist/hooks'
import { useIntl } from '@weenat/wintl'
import { useIsAppAccessRestricted } from 'app/src/hooks/useIsAppAccessRestricted'
import TextNumber from 'app/src/kit/TextNumber'
import isNil from 'lodash-es/isNil'
import { readableColor } from 'polished'
import { memo, useMemo } from 'react'
import { useTheme } from 'styled-components'
import useUniverseBackgroundMapContext from '../universes/useUniverseBackgroundMapContext'
import PureCluster from './PureCluster'

interface DataClusterProps extends Pick<ClusterProperties, 'isVirtualDeviceByMetric'> {
  lat: number
  lng: number
  count: number
  value: number | null
  range: ClusterDisplayValue['range']
  coloringValue: ClusterDisplayValue['coloringValue']
  threshold: ClusterDisplayValue['threshold']
  aggregateType: ClusterAggregateType | null
  waterTankAlertCount: number | null
  onPress: () => void
}

const DataCluster = ({
  onPress,
  isVirtualDeviceByMetric,
  count,
  lat: _lat,
  lng: _lng,
  value,
  range,
  coloringValue,
  threshold,
  aggregateType,
  waterTankAlertCount
}: DataClusterProps) => {
  const { colors } = useTheme()
  const { t } = useIntl()
  const { convertValue } = useConvertedValue()

  const { focusedMetricId, universeAggregate, span } = useUniverseBackgroundMapContext()

  const { isRestricted: isAccessRestrictedByPlan } = useIsAppAccessRestricted({
    isVWS: !isNil(focusedMetricId) && (isVirtualDeviceByMetric.get(focusedMetricId) ?? false)
  })

  const isRestricted = isAccessRestrictedByPlan
    ? isNil(focusedMetricId)
      ? true
      : (isVirtualDeviceByMetric.get(focusedMetricId) ?? true)
        ? !isFreemiumAccessibleAggregate(universeAggregate)
        : true
    : false

  const colorScale = getMarkerColorScale(focusedMetricId, span)

  const backgroundColor = getMarkerBackgroundColor({
    rawValue: coloringValue,
    colorScale,
    colors,
    focusedMetricId,
    threshold,
    isRestricted
  })

  const zIndex = getZIndex({
    metricId: focusedMetricId,
    rawValue: coloringValue,
    // Clusters don't have a favourite concept — always false.
    // Hover elevation is handled by :has(:hover) in PureCluster, not by JS state here.
    isFavorite: false,
    isCluster: true
  })

  const aggregateLabel = useMemo(() => {
    if (isNil(focusedMetricId) || isNil(aggregateType) || aggregateType === 'NONE') {
      return undefined
    }
    if (aggregateType === 'CUSTOM') {
      return !isNil(waterTankAlertCount) && waterTankAlertCount > 0
        ? `⚠️ ${waterTankAlertCount}`
        : '✓'
    }
    if (aggregateType === 'RNG') {
      if (isNil(range)) return undefined
      if (range.min === range.max)
        return convertValue({ metric: focusedMetricId, value: range.min, displayUnit: true })

      const minLabel = convertValue({
        metric: focusedMetricId,
        value: range.min,
        displayUnit: false
      })
      const maxLabel = convertValue({
        metric: focusedMetricId,
        value: range.max,
        displayUnit: true
      })
      return t('numeric.range', { min: minLabel, max: maxLabel })
    }
    if (isNil(value)) return undefined
    const formatted = convertValue({ metric: focusedMetricId, value, displayUnit: true })
    if (aggregateType === 'AVG') {
      return `${t('charts.average_abbrev').toUpperCase()} ${formatted}`
    }
    if (aggregateType === 'MAX') {
      return `${t('charts.max').toUpperCase()} ${formatted}`
    }
    // MIN
    return `${t('charts.min').toUpperCase()} ${formatted}`
  }, [focusedMetricId, aggregateType, value, convertValue, t, waterTankAlertCount, range])

  return (
    <PureCluster density={count} onPress={onPress} color={backgroundColor} zIndex={zIndex}>
      {!isNil(aggregateLabel) ? (
        <TextNumber $fontSize='sm' $color={readableColor(backgroundColor)} $textAlign='center'>
          {aggregateLabel}
        </TextNumber>
      ) : null}
    </PureCluster>
  )
}

export default memo(DataCluster)
